import {getDatabase, push, ref, set, get} from 'firebase/database';
import dayjs from 'dayjs';
import {masteryTasks} from '@pages/dashboard/get-started/mastery-tasks';
import axios from 'axios';
import {authStore} from '../store/stores/auth-store';
import firebaseConfig from './firebase';
import {
  getFirebaseData,
  removeFirebaseData,
  setFirebaseData,
  transactFirebaseData,
  updateFirebaseData,
  writeFirebaseData,
} from './firebase-handler';
export const db = getDatabase(firebaseConfig);

export async function writeData(item: any, path: string) {
  const response = await writeFirebaseData(`${path}/comments`, {
    comment: item.comment,
    edited: item.edited,
    commentBy: item.commentBy,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    replies: [],
  });
  await setFirebaseData(`commentsEntities/${response}`, item.rawComment);
  return response;
}

export const AddSubscribers = async (path: string, item: any) => {
  setFirebaseData(`${path}/subscribers`, item);
};

export const handleCaptureLogin = (
  data: {
    workspaceId: string;
    userId: string;
    userCreatedAt: string;
  },
  action: () => void,
) => {
  const db = getDatabase(firebaseConfig);

  const path = `${FIREBASE_BASE_URL}/${data.workspaceId}/users/${data.userId}/loginEvents/`;

  const storeRedirectComment = localStorage.getItem('unseen_comment');

  // Step 4: Check previous logins
  get(ref(db, path)).then((snapshot) => {
    const loginEvents = snapshot.val();

    // Step 5: Compare timestamps
    const lastLoginTimestamp = loginEvents
      ? (loginEvents as number)
      : Date.parse(data.userCreatedAt);

    const currentTimestamp = Date.now();

    const fourteenDaysInMilliseconds = 14 * 24 * 60 * 60 * 1000;

    if (currentTimestamp - lastLoginTimestamp > fourteenDaysInMilliseconds) {
      action();
      setFirebaseData(`/users/${data.userId}/loginEvents/`, currentTimestamp);
    }
    if (storeRedirectComment) {
      window.location.assign(`${storeRedirectComment}`);
    }
  });
};

export const postComment = async (item: any, key: any, id: any) => {
  try {
    await axios.post(
      `${process.env.REACT_APP_BASE_URL}/notifications/mentions`,
      {
        comment: item.comment,
        goal: id,
        commentBy: item.commentBy,
        commentPath: `objectives/goals?goal=${id}&insight=true&comment=$${key}`,
        users: item.mention,
      },
      {
        headers: {
          Authorization: `Bearer ${authStore?.auth?.tokens.access.token}`,
        },
      },
    );

    return true;
  } catch (error) {
    console.log(error);
  }
};

export const AddMentions = (
  userId: string,
  id: string,
  data: any,
  mentionsPath: string,
) => {
  updateFirebaseData(`users/${userId}/mentions/${mentionsPath}/${id}`, data);
};

export const saveCheckinCommentThreadSubscriptions = (
  path: string,
  subscriberIds: string[],
  mutedSubscriberIds: string[],
) =>
  updateFirebaseData(path, {
    subscribers: subscriberIds.length ? subscriberIds : null,
    mutedSubscribers: mutedSubscriberIds.length ? mutedSubscriberIds : null,
  });

interface CheckinCommentNotificationInput {
  recipientId: string;
  submissionId: string;
  actorId: string;
  isMention: boolean;
  notification?: Record<string, unknown>;
}

export const addCheckinCommentNotification = ({
  recipientId,
  submissionId,
  actorId,
  isMention,
  notification,
}: CheckinCommentNotificationInput) =>
  transactFirebaseData(
    `users/${recipientId}/mentions/checkins/comments/${submissionId}`,
    (currentValue) => {
      const current = currentValue || {};
      const currentMentionedBy = Array.isArray(current.mentionedBy)
        ? current.mentionedBy.filter(Boolean)
        : [];

      return {
        ...current,
        unreadTasks: Number(current.unreadTasks || 0),
        tasksMentionedBy: Array.isArray(current.tasksMentionedBy)
          ? current.tasksMentionedBy
          : [],
        mentionedBy: isMention
          ? [...new Set([...currentMentionedBy, actorId])]
          : currentMentionedBy,
        unreadComment: Number(current.unreadComment || 0) + 1,
        ...(notification ? {notification} : {}),
      };
    },
  );

export const Deletedata = (path: string, e: string) => {
  removeFirebaseData(`${path}/comments/${e}`);
};

export const updateData = (e: any, item: any, path: string) => {
  updateFirebaseData(`${path}/comments/${e}`, {
    comment: item.comment,
    edited: item.edited,
    commentBy: item.commentBy,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
    replies: [],
  });

  setFirebaseData(`commentsEntities/${e}`, item.rawComment);
};

export const ccFirebaseReminderTracker = (userId: string, reviewId: string) => {
  setFirebaseData(
    `users/${authStore.auth.user.id}/review_reminder/${reviewId}`,
    {
      [userId]: new Date(
        new Date().setHours(new Date().getHours() + 1),
      ).getTime(),
    },
  );
};

export const checkinReminderTracker = (userId: string) => {
  writeFirebaseData(`checkin/reminder`, {
    managerId: userId,
    lastSent: new Date(
      new Date().setHours(new Date().getHours() + 2),
    ).getTime(),
  });
};

export const checkinResetTracker = () => {
  setFirebaseData(`checkin/reset`, {
    timestamp: new Date().getTime(),
  });
};

export const updateCheckinReminder = (userId: string, id: string) => {
  updateFirebaseData(`checkin/reminder/${id}`, {
    managerId: userId,
    lastSent: new Date(
      new Date().setHours(new Date().getHours() + 2),
    ).getTime(),
  });
};

// Fn handling general errors on the app
export const logError = (data: any) => {
  const db = getDatabase(firebaseConfig);
  const postListRef = ref(
    db,
    `/error/${process.env.REACT_APP_DEV_ENV}/${dayjs().format('YYYY/MMMM/DD')}`,
  );

  const newPostRef = push(postListRef);

  set(newPostRef, {
    url: data?.url || '',
    error: data?.error || '',
    page: data.page,
    workspace: data.workspace,
    loggedInUser: data.loggedInUser,
    timeCreated: data.createdAt,
  });
};

export const updateMasteryTier = (
  userId: string,
  status: {
    tier: string;
  },
) => {
  updateFirebaseData(`users/${userId}/onboarding/mastery/tier`, status.tier);
};

// State managing home page beam mastery widget
export const dismissBeamMastery = (
  userId: string,
  status: {
    isDismissed: boolean;
  },
) => {
  updateFirebaseData(
    `users/${userId}/onboarding/mastery/isDismissed`,
    status.isDismissed,
  );
};

export const updateTooltipStatus = (
  id: string,
  status: {isDismissed: boolean},
) => {
  const userId = authStore?.auth.user.id;

  updateFirebaseData(`users/${userId}/onboarding/tooltip`, {
    [id]: status.isDismissed,
  });
};

export const beamMasteryTasks = (field: string, value: boolean) => {
  const db = getDatabase(firebaseConfig);
  const userId = authStore?.auth.user.id;

  const path = `users/${userId}/onboarding/mastery/tasks/${field}`;
  const wholePath = `users/${userId}/onboarding/mastery/tasks`;

  get(ref(db, path)).then((snapshot) => {
    const event = snapshot.val();

    if (!event) {
      writeFirebaseData(path, value);
    }
  });

  get(ref(db, wholePath)).then((snapshot) => {
    const events = snapshot.val();
    if (!events) {
      return;
    }
    const count = Object.keys(events)?.length + 1;
    if (count === 3) {
      updateMasteryTier(userId, {tier: 'adept'});
    }
    if (count === masteryTasks().length) {
      updateMasteryTier(userId, {tier: 'master'});
    }
  });
};

export const updateCheckinsGoals = (userId: string, data: any) => {
  writeFirebaseData(`users/${userId}/checkin`, data);
};

export const updateActionItemsFilter = async (userId: string, data: any) => {
  setFirebaseData(`users/${userId}/action_items/filter`, data);
};

export const updateActionItemsActivities = async (data: any) => {
  writeFirebaseData(`activities/action-items`, data);
};

// keep track of KPI value last updated at.

interface KPIUpdate {
  value: number;
  timestamp: number;
  context?: string | null;
}

export const updateKPIHistory = async (
  goalId: string,
  currentValue: number,
  context?: string,
) => {
  try {
    // Get existing data
    const existingData = await getFirebaseData(`goals/${goalId}`);

    const existingHistory: KPIUpdate[] = existingData?.history || [];

    // Create new history entry
    const newUpdate: KPIUpdate = {
      value: currentValue,
      timestamp: Date.now(),
      context: context || null,
    };

    // Add new update to history, keeping last 10 entries
    const updatedHistory = [...existingHistory, newUpdate].slice(-10);

    // Update only the history and lastUpdate fields, not touching updatedAt
    await updateFirebaseData(`goals/${goalId}`, {
      history: updatedHistory,
      lastUpdate: newUpdate,
      updatedAt: existingData?.updatedAt || Date.now(),
    });

    return updatedHistory;
  } catch (error) {
    throw error;
  }
};

export const updateKPITimestamp = async (goalId: string) => {
  try {
    const timestamp = Date.now();

    // Get existing data
    const existingData = await getFirebaseData(`goals/${goalId}`);

    const existingHistory: KPIUpdate[] = existingData?.history || [];

    // Update only the updatedAt field
    await updateFirebaseData(`goals/${goalId}`, {
      updatedAt: timestamp,
      history: existingHistory,
    });

    return timestamp;
  } catch (error) {
    throw error;
  }
};

export const updateKPITracker = async (
  goalId: string,
  updatedAt: number,
  currentValue?: number,
  context?: string,
) => {
  try {
    // First get the existing data to access current history
    const existingData = await getFirebaseData(`goals/${goalId}`);

    // Initialize or get existing history
    const existingHistory: KPIUpdate[] = existingData?.history || [];

    // Create new history entry
    const newUpdate: KPIUpdate = {
      value: currentValue ?? 0,
      timestamp: updatedAt,
      context: context || null,
    };

    // Add new update to history, keeping only the last 10 entries
    const updatedHistory = [...existingHistory, newUpdate].slice(-10);

    // Update Firebase with new timestamp and history
    await updateFirebaseData(`goals/${goalId}`, {
      updatedAt,
      history: updatedHistory,
      lastUpdate: {
        value: currentValue,
        timestamp: updatedAt,
        context: context || null,
      },
    });
  } catch (error) {
    console.error('Error updating KPI tracker:', error);
    throw error;
  }
};

export const updateReviewFramework = (reviewCycleId: string, data: any) => {
  updateFirebaseData(`cc_reviews/${reviewCycleId}`, {frameworks: data});
};

export const updateGoogleTokens = (
  userId: string,
  data: any,
  removeToken?: boolean,
) => {
  if (removeToken) {
    return removeFirebaseData(`users/${userId}/integration`);
  }

  updateFirebaseData(`users/${userId}/integration`, {
    ...data,
    createdAt: dayjs().format(),
  });
};

export const updateMeetingsGoogleEventId = (
  meetingId: string,
  eventsId: string,
) => {
  updateFirebaseData(`meeting/${meetingId}`, {eventId: eventsId});
};

export const notificationHandler = (userId: string, notification: any) => {
  writeFirebaseData(`users/${userId}/notifications`, notification);
};

export const FIREBASE_BASE_URL = `${process.env.REACT_APP_DEV_ENV}/`;

export const database = getDatabase(firebaseConfig);

export const INSIGHT_PATH = 'insights';
